import { unknownFreeBusy } from 'owa-locstrings/lib/strings/unknownfreebusy.locstring.json';
import { locationSuggestionNotAvailable } from 'owa-locstrings/lib/strings/locationsuggestionnotavailable.locstring.json';
import { availableFreeBusy } from 'owa-locstrings/lib/strings/availablefreebusy.locstring.json';
import {
    reservedRoomsState,
    checkingAvailabilitySearchState,
} from './getRoomFreeBusyTextAndStyle.locstring.json';
import loc from 'owa-localize';
import getRecurrenceAvailabilityTextAndStyle from 'owa-location-common-well/lib/utils/getRecurrenceAvailabilityTextAndStyle';
import type { RoomAvailability } from '../RoomSelector.types';
import type { BookingType } from 'owa-location-service';
import { FreeBusyType } from 'owa-freebusy-cache';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';

const baseStyles = require('./RoomFreeBusy.scss');
const opxStyles = require('./RoomFreeBusyOpx.scss');

export default function getRoomFreeBusyTextAndStyle(
    roomAvailability: RoomAvailability,
    bookingType: BookingType
): { text: string; textStyle: string } {
    let { available, busy } = baseStyles;

    /*
        Free/busy colors have been changed to meet a11y/contrast requirements for Room finder Mac/Desktop OPX
        Eventually we would like free/busy colors in OWA to meet a11y requirements as well. Once those colors
        are changed, this check needs to be removed.
        TODO: https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/88585/
    */
    const isDarkMode = getIsDarkTheme();
    if (isHostAppFeatureEnabled('roomFinderFreeBusyStyles')) {
        available = isDarkMode ? opxStyles.availableDarkMode : opxStyles.available;
        busy = isDarkMode ? opxStyles.busyDarkMode : opxStyles.busy;
    }

    // For Reserved Rooms, Show Reserved a Freebusy Status, regardless if is a recurrence meeting
    if (bookingType == 'Reserved') {
        return {
            text: loc(reservedRoomsState),
            textStyle: busy,
        };
    }

    const { freeBusyState, roomRecurrenceAvailability } = roomAvailability;

    // special cases: no data or loading
    if (freeBusyState === FreeBusyType.NoData) {
        return {
            text: loc(unknownFreeBusy),
            textStyle: baseStyles.unknown,
        };
    }

    if (freeBusyState === FreeBusyType.Loading) {
        return {
            text: loc(checkingAvailabilitySearchState),
            textStyle: undefined,
        };
    }

    // All other non free cases (tentative, oof, busy, working elsewhere) should show as unavailable
    if (freeBusyState !== FreeBusyType.Free) {
        return {
            text: loc(locationSuggestionNotAvailable),
            textStyle: busy,
        };
    }

    // Free availability: Either show available or show availability details for recurrence
    const showRecurenceAvailability: boolean =
        isFeatureEnabled('cal-mf-availabilityForRecurrence') && !!roomRecurrenceAvailability;
    if (!showRecurenceAvailability) {
        return {
            text: loc(availableFreeBusy),
            textStyle: available,
        };
    }

    // Show recurrence availability details
    const {
        availableInstancesCount,
        totalInstancesCount,
        isRecurrenceExpansionPartial,
    } = roomRecurrenceAvailability;

    return getRecurrenceAvailabilityTextAndStyle(
        availableInstancesCount,
        totalInstancesCount,
        isRecurrenceExpansionPartial,
        available,
        busy
    );
}
